import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min?url";
import axios from "axios";
import {
  Alert,
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { OrderDocumentDetail } from "../types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

type ChatMessage = {
  content: string;
  documentId?: number;
  documentIds?: number[];
  documentTitle?: string;
  question?: string;
  pageNumber?: number;
  quote?: string;
  highlights?: DocumentHighlight[];
  feedback?: "up" | "down";
  feedbackComments?: string;
  feedbackSaved?: boolean;
  feedbackSaving?: boolean;
  feedbackError?: string;
};

type AskResponse = {
  documentId: number;
  documentTitle?: string;
  answer: string;
  pageNumber?: number;
  quote?: string;
  highlights: DocumentHighlight[];
};

type DocumentHighlight = {
  documentId: number;
  documentTitle?: string;
  lineId: string;
  pageNumber: number;
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

type DocumentFocus = {
  documentId?: number;
  pageNumber?: number;
  highlights: DocumentHighlight[];
  nonce: number;
};

export function DocumentAiReview({
  orderNumber,
  selectedDocuments,
  mobile = false
}: {
  orderNumber: string;
  selectedDocuments: OrderDocumentDetail[];
  mobile?: boolean;
}) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDocuments, setActiveDocuments] = useState<OrderDocumentDetail[]>([]);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState("");
  const [error, setError] = useState("");
  const [activeMessageIndex, setActiveMessageIndex] = useState<number | null>(null);
  const [focusNonce, setFocusNonce] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios.get("/api/documentai/enabled")
      .then(({ data }) => {
        if (!cancelled) {
          setIsEnabled(data?.enabled === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsEnabled(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const drawerTitle = useMemo(() => {
    if (activeDocuments.length === 1) {
      return activeDocuments[0].documentDescription || `Document ${activeDocuments[0].id}`;
    }

    return `${activeDocuments.length} selected documents`;
  }, [activeDocuments]);

  const activeMessage = activeMessageIndex == null ? null : messages[activeMessageIndex];
  const documentFocus: DocumentFocus = {
    documentId: activeMessage?.documentId,
    pageNumber: activeMessage?.pageNumber,
    highlights: activeMessage?.highlights ?? [],
    nonce: focusNonce
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.setTimeout(() => {
      threadEndRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    }, 50);
  }, [isOpen, isLoading, messages.length, activeMessageIndex]);

  if (!isEnabled) {
    return null;
  }

  const openDrawer = () => {
    if (!selectedDocuments.length) {
      window.alert("No documents have been selected");
      return;
    }

    setActiveDocuments(selectedDocuments);
    setMessages([]);
    setQuestion("");
    setPendingQuestion("");
    setError("");
    setActiveMessageIndex(null);
    setFocusNonce(0);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setActiveDocuments([]);
    setMessages([]);
    setQuestion("");
    setPendingQuestion("");
    setError("");
    setActiveMessageIndex(null);
    setFocusNonce(0);
  };

  const submitQuestion = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || isLoading || activeDocuments.length === 0) {
      return;
    }

    setQuestion("");
    setError("");
    setIsLoading(true);
    setPendingQuestion(trimmedQuestion);

    try {
      const { data } = await axios.post<AskResponse>("/api/documentai/ask", {
        documentIds: activeDocuments.map((document) => document.id),
        question: trimmedQuestion
      });

      setMessages((current) => {
        const next = [
          ...current,
          {
            content: data.answer,
            question: trimmedQuestion,
            documentId: data.documentId,
            documentIds: activeDocuments.map((document) => document.id),
            documentTitle: data.documentTitle,
            pageNumber: data.pageNumber,
            quote: data.quote,
            highlights: data.highlights ?? []
          }
        ];
        setActiveMessageIndex(next.length - 1);
        setFocusNonce((current) => current + 1);
        return next;
      });
    } catch {
      setError("The AI review could not complete. Try again or choose a smaller document set.");
    } finally {
      setIsLoading(false);
      setPendingQuestion("");
    }
  };

  const updateMessage = (index: number, changes: Partial<ChatMessage>) => {
    setMessages((current) => current.map((message, messageIndex) => (
      messageIndex === index ? { ...message, ...changes } : message
    )));
  };

  const saveFeedback = async (message: ChatMessage, index: number, isHelpful: boolean) => {
    if (!message.documentId || !message.question || message.feedbackSaving) {
      return;
    }

    const comments = message.feedbackComments?.trim();
    if (!isHelpful && !comments) {
      updateMessage(index, {
        feedback: "down",
        feedbackError: "Add a comment before saving thumbs down feedback."
      });
      return;
    }

    updateMessage(index, {
      feedback: isHelpful ? "up" : "down",
      feedbackSaving: true,
      feedbackError: ""
    });

    try {
      await axios.post("/api/documentai/feedback", {
        orderNumber,
        documentIds: message.documentIds ?? activeDocuments.map((document) => document.id),
        question: message.question,
        answer: message.content,
        isHelpful,
        comments: isHelpful ? undefined : comments,
        quote: message.quote,
        pageNumber: message.pageNumber
      });

      updateMessage(index, {
        feedback: isHelpful ? "up" : "down",
        feedbackSaved: true,
        feedbackSaving: false,
        feedbackComments: comments,
        feedbackError: ""
      });
    } catch {
      updateMessage(index, {
        feedbackSaving: false,
        feedbackSaved: false,
        feedbackError: "Could not save feedback."
      });
    }
  };

  return (
    <>
      <Badge
        badgeContent={selectedDocuments.length}
        color="primary"
        invisible={selectedDocuments.length === 0}
        sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}
      >
        <Button
          size="small"
          variant={mobile ? "outlined" : "contained"}
          onClick={openDrawer}
          startIcon={<Iconify icon="eva:flash-fill" width={15} />}
          sx={{
            height: mobile ? undefined : 26,
            minWidth: "auto",
            px: mobile ? 1 : 1.25,
            py: mobile ? undefined : 0.5,
            fontSize: mobile ? undefined : 12,
            fontWeight: 700,
            textTransform: "none"
          }}
        >
          Ask AI
        </Button>
      </Badge>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: "100vw",
            maxWidth: "100vw",
            height: "100vh",
            display: "grid",
            gridTemplateRows: "auto minmax(0, 1fr)",
            overflow: "hidden"
          }
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Ask AI about selected documents
            </Typography>
            <Typography variant="h6" sx={{ color: "primary.dark", fontWeight: 700 }} noWrap>
              {drawerTitle}
            </Typography>
          </Box>
          <IconButton onClick={closeDrawer}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.65fr) minmax(380px, 0.85fr)" },
            minHeight: 0,
            height: "100%",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: "#242b38",
              p: 2,
              overscrollBehavior: "contain",
              display: { xs: "none", lg: "block" }
            }}
          >
            {activeDocuments.length > 0 ? (
              <MultiPdfDocumentViewer documents={activeDocuments} focus={documentFocus} />
            ) : (
              <Alert severity="info">Select documents, then ask a question.</Alert>
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateRows: "auto auto minmax(0, 1fr)",
              minHeight: 0,
              backgroundColor: "background.neutral"
            }}
          >
            <Box component="form" onSubmit={submitQuestion} sx={{ p: 2, backgroundColor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700 }}>
                  New independent question
                </Typography>
                <Stack direction="row" spacing={1} alignItems="stretch">
                  <TextField
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    placeholder="Ask what these documents say about fees, parties, dates..."
                    disabled={isLoading}
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                  />
                  <Button type="submit" variant="contained" disabled={isLoading || !question.trim()}>
                    {isLoading ? "Reading" : "Ask"}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

            <Stack spacing={1.5} sx={{ p: 2, minHeight: 0, overflow: "auto" }}>
              {messages.length === 0 && !isLoading && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderStyle: "dashed",
                    backgroundColor: "background.paper"
                  }}
                >
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    AI Review
                  </Typography>
                  <Typography variant="body2" sx={{ color: "primary.dark", mt: 0.5 }}>
                    Each question starts a fresh AI review of the selected files. TitleQ will OCR the documents and cite where the answer came from.
                  </Typography>
                </Paper>
              )}

              {messages.map((message, index) => (
                <Paper
                  key={`${message.documentId}-${message.question}-${index}`}
                  variant="outlined"
                  sx={{
                    justifySelf: "flex-start",
                    width: "100%",
                    maxWidth: 620,
                    p: 1.5,
                    borderRadius: 0.75,
                    backgroundColor: "background.paper",
                    borderColor: "divider",
                    boxShadow: "0 10px 22px rgba(0, 14, 72, 0.06)"
                  }}
                >
                  <Stack spacing={0.9}>
                    <Box sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 0.75, backgroundColor: "#f7f7f9" }}>
                      <Typography variant="caption" sx={{ color: "#707898", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                        Question
                      </Typography>
                      <Typography
                        sx={{
                          color: "primary.dark",
                          fontSize: "13px !important",
                          lineHeight: "1.45 !important",
                          m: 0,
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        {message.question}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#707898", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                        TitleQ AI answer
                      </Typography>
                      <Typography
                        sx={{
                          color: "primary.dark",
                          fontSize: "13px !important",
                          lineHeight: "1.5 !important",
                          m: 0,
                          mt: 0.4,
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Box>
                    {message.quote && (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          setActiveMessageIndex(index);
                          setFocusNonce((current) => current + 1);
                        }}
                        sx={{
                          display: "-webkit-box",
                          width: "100%",
                          mt: 0.25,
                          px: "9px",
                          py: "8px",
                          borderColor: "#c9cedb",
                          borderStyle: "solid",
                          borderWidth: 1,
                          borderRadius: "4px",
                          backgroundColor: "#f7f7f9",
                          color: "primary.dark",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: "12px !important",
                          fontWeight: 700,
                          lineHeight: "1.35 !important",
                          textAlign: "left",
                          whiteSpace: "normal !important",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          "&:hover": {
                            borderColor: "primary.dark",
                            backgroundColor: "#eef0f6"
                          }
                        }}
                      >
                        {message.documentTitle || `Document ${message.documentId || "?"}`} · Page {message.pageNumber || "?"} · “{message.quote}”
                      </Box>
                    )}
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mt: 0.75, pt: 1, borderTop: "1px solid", borderColor: "#eef0f6" }}
                    >
                      <Tooltip title="Helpful">
                        <span>
                          <Box
                            component="button"
                            type="button"
                            aria-label="Helpful"
                            disabled={message.feedbackSaving || message.feedbackSaved}
                            onClick={() => saveFeedback(message, index, true)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 30,
                              height: 28,
                              p: 0,
                              border: "1px solid",
                              borderColor: message.feedback === "up" ? "primary.dark" : "#c9cedb",
                              borderRadius: "4px",
                              backgroundColor: message.feedback === "up" ? "#eef0f6" : "#fff",
                              color: "primary.dark",
                              cursor: message.feedbackSaving || message.feedbackSaved ? "default" : "pointer",
                              "&:hover": {
                                borderColor: "primary.dark",
                                backgroundColor: "#eef0f6"
                              },
                              "&:disabled": {
                                opacity: message.feedbackSaved && message.feedback !== "up" ? 0.45 : 1
                              }
                            }}
                          >
                            <Iconify icon="material-symbols:thumb-up-outline" width={17} />
                          </Box>
                        </span>
                      </Tooltip>
                      <Tooltip title="Not helpful">
                        <span>
                          <Box
                            component="button"
                            type="button"
                            aria-label="Not helpful"
                            disabled={message.feedbackSaving || message.feedbackSaved}
                            onClick={() => updateMessage(index, { feedback: "down", feedbackError: "" })}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 30,
                              height: 28,
                              p: 0,
                              border: "1px solid",
                              borderColor: message.feedback === "down" ? "primary.dark" : "#c9cedb",
                              borderRadius: "4px",
                              backgroundColor: message.feedback === "down" ? "#eef0f6" : "#fff",
                              color: "primary.dark",
                              cursor: message.feedbackSaving || message.feedbackSaved ? "default" : "pointer",
                              "&:hover": {
                                borderColor: "primary.dark",
                                backgroundColor: "#eef0f6"
                              },
                              "&:disabled": {
                                opacity: message.feedbackSaved && message.feedback !== "down" ? 0.45 : 1
                              }
                            }}
                          >
                            <Iconify icon="material-symbols:thumb-down-outline" width={17} />
                          </Box>
                        </span>
                      </Tooltip>
                      {message.feedbackSaving && <Typography variant="caption">Saving</Typography>}
                      {message.feedbackSaved && <Typography variant="caption">Saved</Typography>}
                    </Stack>

                    {message.feedback === "down" && !message.feedbackSaved && (
                      <Stack spacing={1}>
                        <TextField
                          value={message.feedbackComments ?? ""}
                          onChange={(event) => updateMessage(index, {
                            feedbackComments: event.target.value,
                            feedbackError: ""
                          })}
                          placeholder="What should TitleQ AI have done better?"
                          multiline
                          minRows={3}
                          size="small"
                          disabled={message.feedbackSaving}
                          fullWidth
                        />
                        <Button
                          type="button"
                          size="small"
                          variant="contained"
                          disabled={message.feedbackSaving || !message.feedbackComments?.trim()}
                          onClick={() => saveFeedback(message, index, false)}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Save feedback
                        </Button>
                      </Stack>
                    )}

                    {message.feedbackError && <Alert severity="error">{message.feedbackError}</Alert>}
                  </Stack>
                </Paper>
              ))}

              {isLoading && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, backgroundColor: "background.paper" }}>
                  <Stack spacing={1}>
                    <Box sx={{ p: 1, borderRadius: 1, backgroundColor: "background.neutral" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                        Question
                      </Typography>
                      <Typography variant="body2" sx={{ color: "primary.dark", whiteSpace: "pre-wrap" }}>
                        {pendingQuestion}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        gap: "5px",
                        width: "max-content",
                        p: "10px 12px",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px",
                        backgroundColor: "background.paper",
                        "@keyframes titleq-ai-pulse": {
                          "0%, 80%, 100%": {
                            opacity: 0.35,
                            transform: "translateY(0)"
                          },
                          "40%": {
                            opacity: 1,
                            transform: "translateY(-4px)"
                          }
                        },
                        "& span": {
                          width: 7,
                          height: 7,
                          borderRadius: "999px",
                          backgroundColor: "primary.dark",
                          animation: "titleq-ai-pulse 900ms infinite ease-in-out"
                        },
                        "& span:nth-of-type(2)": {
                          animationDelay: "120ms"
                        },
                        "& span:nth-of-type(3)": {
                          animationDelay: "240ms"
                        }
                      }}
                      aria-label="Reading selected documents"
                    >
                      <span />
                      <span />
                      <span />
                    </Box>
                  </Stack>
                </Paper>
              )}
              <Box ref={threadEndRef} />
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

function MultiPdfDocumentViewer({
  documents,
  focus
}: {
  documents: OrderDocumentDetail[];
  focus: DocumentFocus;
}) {
  return (
    <Stack spacing={2.5}>
      {documents.map((document, index) => (
        <PdfDocumentViewer
          key={document.id}
          document={document}
          position={index + 1}
          total={documents.length}
          focus={focus.documentId === document.id ? focus : { highlights: [], nonce: focus.nonce }}
        />
      ))}
    </Stack>
  );
}

function PdfDocumentViewer({
  document,
  position,
  total,
  focus
}: {
  document: OrderDocumentDetail;
  position: number;
  total: number;
  focus: DocumentFocus;
}) {
  const [pdf, setPdf] = useState<any>(null);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);
  const [pageWidth, setPageWidth] = useState(720);
  const [error, setError] = useState("");
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
    setPdf(null);
    setPageNumbers([]);
    setError("");

    const loadPdf = async () => {
      try {
        const response = await fetch(`/opendoc?id=${document.id}`);
        if (!response.ok) {
          throw new Error("Could not load PDF.");
        }

        const data = await response.arrayBuffer();
        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({
          data,
          cMapPacked: true,
          cMapUrl: "/pdfjs/cmaps/",
          iccUrl: "/pdfjs/iccs/",
          standardFontDataUrl: "/pdfjs/standard_fonts/",
          useWasm: true,
          wasmUrl: "/pdfjs/wasm/"
        });
        const loadedPdf = await loadingTask.promise;

        if (cancelled) {
          return;
        }

        setPdf(loadedPdf);
        setPageNumbers(Array.from({ length: loadedPdf.numPages }, (_, index) => index + 1));
      } catch {
        if (!cancelled) {
          setError("Could not render this PDF.");
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
    };
  }, [document.id]);

  useEffect(() => {
    const updateWidth = () => {
      const containerWidth = viewerRef.current?.clientWidth ?? 760;
      setPageWidth(Math.max(320, Math.min(980, containerWidth - 36)));
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const targetPageNumber = focus.pageNumber ?? focus.highlights[0]?.pageNumber;
    if (!targetPageNumber) {
      return;
    }

    window.setTimeout(() => {
      pageRefs.current[targetPageNumber]?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }, 100);
  }, [focus.nonce, focus.pageNumber, focus.highlights]);

  return (
    <Stack ref={viewerRef} component="section" spacing={1.25} sx={{ minWidth: 0 }}>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1, backgroundColor: "background.paper" }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          Document {position} of {total}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700 }}>
          {document.documentDescription || `Document ${document.id}`}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {document.numPages ? `${document.numPages} pages` : "PDF preview"}
        </Typography>
      </Paper>

      {error && (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          {error}
        </Paper>
      )}

      {!error && !pdf && (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          Loading PDF...
        </Paper>
      )}

      {pdf && pageNumbers.map((pageNumber) => (
        <PdfPage
          key={`${document.id}-${pageNumber}`}
          pdf={pdf}
          pageNumber={pageNumber}
          targetWidth={pageWidth}
          highlights={focus.highlights.filter((highlight) => highlight.pageNumber === pageNumber)}
          setPageRef={(element) => {
            pageRefs.current[pageNumber] = element;
          }}
        />
      ))}
    </Stack>
  );
}

function PdfPage({
  pdf,
  pageNumber,
  targetWidth,
  highlights,
  setPageRef
}: {
  pdf: any;
  pageNumber: number;
  targetWidth: number;
  highlights: DocumentHighlight[];
  setPageRef: (element: HTMLDivElement | null) => void;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(pageNumber <= 2);

  useEffect(() => {
    const element = shellRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true);
      }
    }, {
      rootMargin: "2400px 0px"
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<void> } | null = null;

    const render = async () => {
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = targetWidth / baseViewport.width;
      const cssViewport = page.getViewport({ scale: cssScale });
      setSize({ width: cssViewport.width, height: cssViewport.height });

      if (!isVisible || cancelled) {
        return;
      }

      const pixelRatio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) {
        return;
      }

      const context = canvas.getContext("2d");
      if (!context) {
        return;
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${cssViewport.width}px`;
      canvas.style.height = `${cssViewport.height}px`;

      const currentRenderTask = page.render({ canvasContext: context, viewport });
      renderTask = currentRenderTask;
      try {
        await currentRenderTask.promise;
      } catch (error) {
        if (!cancelled) {
          throw error;
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, targetWidth, isVisible]);

  return (
    <Box
      ref={(element: HTMLDivElement | null) => {
        shellRef.current = element;
        setPageRef(element);
      }}
      sx={{ display: "grid", justifyItems: "center", gap: 1, mb: 1.5 }}
    >
      <Typography variant="caption" sx={{ justifySelf: "flex-start", color: "#dfe1e6", fontWeight: 700 }}>
        Page {pageNumber}
      </Typography>
      <Box sx={{ position: "relative", backgroundColor: "common.white", overflow: "hidden", borderRadius: 0.5 }}>
        <canvas ref={canvasRef} style={{ display: "block" }} />
        {!isVisible && size.height > 0 && (
          <Box sx={{ width: size.width, height: size.height, backgroundColor: "grey.100" }} />
        )}
        {size.width > 0 && highlights.map((highlight) => (
          <Box
            key={highlight.lineId}
            title={highlight.text}
            sx={{
              position: "absolute",
              left: `${highlight.left * size.width}px`,
              top: `${highlight.top * size.height}px`,
              width: `${highlight.width * size.width}px`,
              height: `${highlight.height * size.height}px`,
              border: "1px solid rgba(245, 158, 11, 0.75)",
              borderRadius: 0.25,
              backgroundColor: "rgba(251, 191, 36, 0.28)",
              boxShadow: "0 0 0 2px rgba(251, 191, 36, 0.14)",
              pointerEvents: "none"
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
